import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import styles from './ProfilePage.module.css';
import MountainCard from '../components/MountainCard'; // Adjusted path
import GearCard from '../components/GearCard';
import API_BASE_URL from '../config/api';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom'; // Import useParams
import { buildStorageUrl } from '../config/firebaseStorage';
import { auth } from '../firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { likePost, unlikePost } from '../services/postService';
import PostCard from '../components/PostCard';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import ProfilePageSkeleton from '../components/ProfilePageSkeleton';

function ProfilePage({ mountains }) {
  const { currentUser, addToWishlist, markAsSummited, removeFromWishlist, unmarkSummited, followUser, unfollowUser } = useUser();
  const { username: urlUsername } = useParams(); // Get username from URL parameters
  const { t } = useTranslation();

  const [displayUser, setDisplayUser] = useState(null); // The user whose profile is being displayed
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [userHighestPoint, setUserHighestPoint] = useState(0);
  const [userSummitedCount, setUserSummitedCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [watchlistedGear, setWatchlistedGear] = useState([]);
  const [loadingWatchlistedGear, setLoadingWatchlistedGear] = useState(false);

  // Responsive pagination state
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const initialCount = isMobile ? 3 : 5;
  const incrementCount = initialCount;

  const [visiblePostsCount, setVisiblePostsCount] = useState(initialCount);
  const [visibleWishlistCount, setVisibleWishlistCount] = useState(initialCount);
  const [visibleGearCount, setVisibleGearCount] = useState(initialCount);
  const [visibleSummitedCount, setVisibleSummitedCount] = useState(initialCount);
  const [visibleUploadedCount, setVisibleUploadedCount] = useState(initialCount);

  // Change password local state (only for currentUser's own profile)
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoadingProfile(true);
      setProfileError('');
      let userToDisplay = null;

      if (urlUsername && (!currentUser || currentUser.username !== urlUsername)) {
        // Fetch public profile if URL has username and it's not the current user
        try {
          const response = await fetch(`${API_BASE_URL}/api/users/${urlUsername}`);
          const data = await response.json();
          if (data.success) {
            userToDisplay = data.data;
          } else {
            setProfileError(data.message || t('profile.errors.fetch'));
          }
        } catch (error) {
          console.error('Error fetching public profile:', error);
          setProfileError(t('profile.errors.network'));
        }
      } else {
        // Display current user's profile
        userToDisplay = currentUser;
      }

      setDisplayUser(userToDisplay);
      setLoadingProfile(false);
    };

    fetchUserProfile();
  }, [urlUsername, currentUser, t]);

  useEffect(() => {
    const fetchUserPosts = async () => {
      if (!displayUser || !displayUser.username) return;
      setLoadingPosts(true);
      try {
        const postsQuery = query(
          collection(db, 'posts'),
          where('username', '==', displayUser.username),
          orderBy('timestamp', 'desc')
        );
        const snapshot = await getDocs(postsQuery);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUserPosts(data);
      } catch (err) {
        console.error('Failed to load user posts:', err);
        setUserPosts([]);
      } finally {
        setLoadingPosts(false);
      }
    };

    fetchUserPosts();
  }, [displayUser]);

  useEffect(() => {
    const watchlistIds = Array.isArray(displayUser?.gearWatchlist)
      ? displayUser.gearWatchlist.map((itemId) => String(itemId || '').trim()).filter(Boolean)
      : [];

    if (watchlistIds.length === 0) {
      setWatchlistedGear([]);
      setLoadingWatchlistedGear(false);
      return;
    }

    let isCancelled = false;

    const fetchWatchlistedGear = async () => {
      setLoadingWatchlistedGear(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/gear?currency=EUR`);
        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || 'Failed to fetch gear watchlist');
        }

        const allGear = Array.isArray(payload.data) ? payload.data : [];
        const byId = new Map(allGear.map((gearItem) => [String(gearItem?.id || '').trim(), gearItem]));
        const orderedGear = watchlistIds
          .map((watchId) => byId.get(watchId))
          .filter(Boolean)
          .slice(0, 10);

        if (!isCancelled) {
          setWatchlistedGear(orderedGear);
        }
      } catch (error) {
        console.error('Error loading watchlisted gear:', error);
        if (!isCancelled) {
          setWatchlistedGear([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingWatchlistedGear(false);
        }
      }
    };

    fetchWatchlistedGear();

    return () => {
      isCancelled = true;
    };
  }, [displayUser?.gearWatchlist]);

  const handleTogglePostLike = async (post) => {
    if (!currentUser?.uid) {
      // optionally show login prompt
      return;
    }

    const userId = currentUser.uid;
    const alreadyLiked = Boolean(post?.likes?.[userId]);

    setUserPosts(prev => prev.map(p => {
      if (p.id !== post.id) return p;
      const nextLikes = { ...(p.likes || {}) };
      if (alreadyLiked) delete nextLikes[userId]; else nextLikes[userId] = new Date();
      return { ...p, likes: nextLikes };
    }));

    try {
      if (alreadyLiked) {
        await unlikePost(post.id, userId);
      } else {
        await likePost(post.id, userId);
      }
    } catch (err) {
      console.error('Failed to toggle like on profile post:', err);
      // revert on error
      setUserPosts(prev => prev.map(p => p.id === post.id ? post : p));
    }
  };

  useEffect(() => {
    // Compute user stats locally from their data and mountains
    if (!displayUser || !mountains.length) {
      setUserHighestPoint(0);
      setUserSummitedCount(0);
      return;
    }

    // Calculate highest point from user's summited mountains
    let highestPoint = 0;
    const summitedMountainsForDisplayUser = mountains.filter(mountain =>
      displayUser.summited?.includes(mountain.id)
    );

    summitedMountainsForDisplayUser.forEach(mountain => {
      if (mountain.height > highestPoint) {
        highestPoint = mountain.height;
      }
    });

    setUserHighestPoint(highestPoint);
    setUserSummitedCount(summitedMountainsForDisplayUser.length);

  }, [displayUser, mountains]);


  const handleFollowToggle = async () => {
    if (!currentUser) {
        // Redirect to login
        window.location.href = '/login'; 
        return;
    }
    const targetId = displayUser.authUid || displayUser._id || displayUser.uid;
    const isNowFollowing = currentUser.following?.includes(targetId);

    setFollowLoading(true);
    try {
        if (isNowFollowing) {
            await unfollowUser(displayUser.username);
        } else {
            await followUser(displayUser.username);
        }
    } catch (e) {
        console.error(e);
    } finally {
        setFollowLoading(false);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdError('');
    setPwdSuccess('');

    if (!currentUser || currentUser.username !== displayUser.username) {
        setPwdError(t('profile.passwordErrors.self'));
        return;
    }

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPwdError(t('profile.passwordErrors.required'));
      return;
    }
    if (newPassword.length < 8) {
      setPwdError(t('profile.passwordErrors.length'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError(t('profile.passwordErrors.mismatch'));
      return;
    }

    try {
      setPwdLoading(true);
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error(t('profile.passwordErrors.reauth'));
      }

      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setPwdSuccess(t('profile.passwordSuccess'));
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwdError(err.message || t('profile.passwordErrors.generic'));
    } finally {
      setPwdLoading(false);
    }
  };

  if (loadingProfile) {
    return <ProfilePageSkeleton />;
  }

  if (profileError) {
    return (
      <div className={styles.profilePage}>
        <p className={styles.error}>{profileError}</p>
      </div>
    );
  }

  if (!displayUser) {
    return (
      <div className={styles.profilePage}>
        <div className={styles.notLoggedIn}>
          <h2>{t('profile.notFoundTitle')}</h2>
          <p>{t('profile.notFoundBody')}</p>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser && currentUser.username === displayUser.username;
  const isFollowing = displayUser && currentUser?.following?.includes(displayUser.authUid || displayUser._id || displayUser.uid);

  const wishlistMountains = mountains.filter(mountain =>
    displayUser.wishlist?.includes(mountain.id)
  );

  const summitedMountains = mountains.filter(mountain =>
    displayUser.summited?.includes(mountain.id)
  );

  const uploadedMountains = mountains.filter(mountain =>
    mountain.uploadedBy === displayUser.username
  );
  const postsCount = userPosts.length;

  const displayName = displayUser.nickname && displayUser.nickname.trim().length > 0
    ? displayUser.nickname
    : displayUser.username;

  return (
    <>
      <Helmet>
        <title>
          {isOwnProfile
            ? t('profile.helmetOwn', { name: displayName })
            : t('profile.helmetPublic', { name: displayName })}
        </title>
        <meta
          name="description"
          content={t('profile.helmetDescription', {
            name: displayName,
            wishlist: wishlistMountains.length,
            summited: summitedMountains.length,
            uploaded: uploadedMountains.length
          })}
        />
      </Helmet>
    <div className={styles.profilePage}>
      <div className={styles.profileHeader}>
        <div className={styles.profileHeaderLeft}>
          <div className={styles.profilePictureFrame}>
            <img
              src={
                displayUser.pfp && displayUser.pfp.trim() !== ""
                  ? displayUser.pfp
                  : buildStorageUrl('pfp/placeholderP.png')
              }
              alt={t('profile.avatarAlt', { name: displayName })}
              className={styles.profilePicture}
            />
          </div>
        </div>

        <div className={styles.profileHeaderRight}>
          <div className={styles.profileTitleContainer}>
            <h1>
              {isOwnProfile ? t('profile.welcome', { name: displayName }) : displayName}
            </h1>
            <p className={styles.username}>@{displayUser.username}</p>
          </div>

          <div className={styles.profileInfoSection}>
            {displayUser.socials && displayUser.socials.length > 0 && (
              <div className={styles.socialLinks}>
                {displayUser.socials.map((social, index) => {
                  if (social.type === 'website' && social.url) {
                    return (
                      <a
                        key={`website-${index}`}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.socialIcon} ${styles.website}`}
                        title={t('profile.socials.website')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="2rem" height="2rem" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
                        </svg>
                      </a>
                    );
                  }
                  if (social.type === 'youtube' && social.url) {
                    return (
                      <a
                        key={`youtube-${index}`}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.socialIcon} ${styles.youtube}`}
                        title={t('profile.socials.youtube')}
                      >
                        <svg role="img" viewBox="0 0 24 24" width="2rem" height="2rem" xmlns="http://www.w3.org/2000/svg" fill="currentColor"><title>YouTube</title><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                      </a>
                    );
                  }
                  if (social.type === 'instagram' && social.handle) {
                    return (
                      <a
                        key={`instagram-${index}`}
                        href={`https://www.instagram.com/${social.handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.socialIcon} ${styles.instagram}`}
                        title={t('profile.socials.instagram')}
                      >
                        <svg role="img" viewBox="0 0 24 24" width="2rem" height="2rem" xmlns="http://www.w3.org/2000/svg" fill="currentColor"><title>Instagram</title><path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077"/></svg>
                      </a>
                    );
                  }
                  return null;
                })}
              </div>
            )}

            {displayUser.bio && (
              <div className={styles.bioContainer}>
                <p className={styles.bioText}>
                  {showFullBio || displayUser.bio.length <= 100
                    ? displayUser.bio
                    : `${displayUser.bio.slice(0, 100)}…`}
                </p>
                {displayUser.bio.length > 100 && (
                  <button
                    onClick={() => setShowFullBio(prev => !prev)}
                    className={styles.readMoreButton}
                  >
                    {showFullBio ? t('profile.bio.readLess') : t('profile.bio.readMore')}
                  </button>
                )}
              </div>
            )}

            {isOwnProfile ? (
              <div className={styles.editButtonContainer}>
                <a
                  href="/settings"
                  className={styles.editProfileButton}
                >
                  {t('common.settings', { defaultValue: 'Settings' })}
                </a>
              </div>
            ) : (
              displayUser && (displayUser.authUid || displayUser._id || displayUser.uid) && (
                <div className={styles.editButtonContainer}>
                  <button 
                     onClick={handleFollowToggle} 
                     disabled={followLoading}
                     className={isFollowing ? `${styles.followButton} ${styles.followingButton}` : styles.followButton}
                  >
                     {followLoading ? '...' : (isFollowing ? t('profile.unfollow', 'Unfollow') : t('profile.follow', 'Follow'))}
                  </button>
                  {currentUser && !currentUser.isVerified ? (
                      <span className={styles.messageButton} style={{ opacity: 0.5, cursor: 'not-allowed' }} title="Verify your email to unlock messaging">
                        Message (Locked)
                      </span>
                  ) : (
                    <a
                      href={`/chats?user=${displayUser.authUid || displayUser._id || displayUser.uid}`}
                      className={styles.messageButton}
                    >
                      {t('profile.messageUser', 'Message')}
                    </a>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <div className={styles.stats}>
        {isOwnProfile && (
            <div className={styles.statCard}>
                <h3>{wishlistMountains.length}</h3>
                <p>{t('profile.stats.wishlist')}</p>
            </div>
        )}
        <div className={styles.statCard}>
          <h3>{postsCount}</h3>
          <p>{t('profile.stats.posts') || 'Posts'}</p>
        </div>
        <div className={styles.statCard}>
          <h3>{summitedMountains.length}</h3>
          <p>{t('profile.stats.summited')}</p>
        </div>

        <div className={styles.statCard}>
          <h3>{userHighestPoint}m</h3>
          <p>{t('profile.stats.highestPoint')}</p>
        </div>
        <div className={styles.statCard}>
          <h3>{userSummitedCount}</h3>
          <p>{t('profile.stats.totalSummits')}</p>
        </div>
      </div>

      <div className={styles.sections}>
        {/* Posts Section */}
        {userPosts.length > 0 && (
          <section className={styles.section}>
            <h2>
              <svg style={{ width: '1.5rem', height: '1.5rem', marginBottom: '-0.25rem' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clipRule="evenodd" />
              </svg> {t('profile.sections.posts', { defaultValue: 'Posts ({{count}})', count: userPosts.length })}
            </h2>
            {loadingPosts ? (
              <p>Loading posts...</p>
            ) : (
              <div className={styles.postsList}>
                {userPosts.slice(0, visiblePostsCount).map(p => (
                  <PostCard key={p.id} post={p} currentUserId={currentUser?.uid || ''} onToggleLike={() => handleTogglePostLike(p)} />
                ))}
                {userPosts.length > visiblePostsCount && (
                  <div className={styles.showMoreContainer}>
                    <button
                      className={styles.showMoreButton}
                      onClick={() => setVisiblePostsCount(prev => prev + initialCount)}
                    >
                      {t('profile.showMore', 'Show More')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Wishlist Section */}
        {wishlistMountains.length > 0 && (
          <section className={styles.section}>
            <h2><svg style={{ width: '1.5rem', height: '1.5rem', marginBottom: '-0.25rem' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
              <path fillRule="evenodd" d="M6.32 2.577a49.255 49.255 0 0 1 11.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 0 1-1.085.67L12 18.089l-7.165 3.583A.75.75 0 0 1 3.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93Z" clipRule="evenodd" />
            </svg> {t('profile.sections.wishlist', { count: wishlistMountains.length })}</h2>
            <div className={styles.mountainsGrid}>
              {wishlistMountains.slice(0, visibleWishlistCount).map(mountain => (
                <MountainCard key={mountain.id} mountain={mountain} />
              ))}
              {wishlistMountains.length > visibleWishlistCount && (
                <div className={styles.showMoreContainer}>
                  <button
                    className={styles.showMoreButton}
                    onClick={() => setVisibleWishlistCount(prev => prev + initialCount)}
                  >
                    {t('profile.showMore', 'Show More')}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Gear Watchlist Section */}
        {watchlistedGear.length > 0 && (
          <section className={styles.section}>
            <h2>
              <svg style={{ width: '1.5rem', height: '1.5rem', marginBottom: '-0.25rem' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                <path d="M11.48 3.499a.75.75 0 0 1 1.04 0l2.125 2.03 2.929-.428a.75.75 0 0 1 .832.596l.52 2.909 2.654 1.314a.75.75 0 0 1 .34 1.006l-1.26 2.68 1.26 2.68a.75.75 0 0 1-.34 1.006l-2.654 1.314-.52 2.909a.75.75 0 0 1-.832.596l-2.93-.428-2.124 2.03a.75.75 0 0 1-1.04 0l-2.124-2.03-2.93.428a.75.75 0 0 1-.832-.596l-.52-2.909-2.654-1.314a.75.75 0 0 1-.34-1.006l1.26-2.68-1.26-2.68a.75.75 0 0 1 .34-1.006l2.654-1.314.52-2.909a.75.75 0 0 1 .832-.596l2.93.428 2.124-2.03Z" />
              </svg> {t('profile.sections.gearWatchlist', { defaultValue: 'Watchlisted gear ({{count}})', count: watchlistedGear.length })}
            </h2>

            {loadingWatchlistedGear ? (
              <div className={styles.emptyState}>
                <p>{t('common.loading', { defaultValue: 'Loading...' })}</p>
              </div>
            ) : (
              <div className={styles.mountainsGrid}>
                {watchlistedGear.slice(0, visibleGearCount).map((gearItem) => (
                  <GearCard key={gearItem.id} gear={gearItem} />
                ))}
                {watchlistedGear.length > visibleGearCount && (
                  <div className={styles.showMoreContainer}>
                    <button
                      className={styles.showMoreButton}
                      onClick={() => setVisibleGearCount(prev => prev + initialCount)}
                    >
                      {t('profile.showMore', 'Show More')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Summited Section */}
        {summitedMountains.length > 0 && (
          <section className={styles.section}>
            <h2><svg style={{ width: '1.5rem', height: '1.5rem', marginBottom: '-0.25rem' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
              <path fillRule="evenodd" d="M7.502 6h7.128A3.375 3.375 0 0 1 18 9.375v9.375a3 3 0 0 0 3-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 0 0-.673-.05A3 3 0 0 0 15 1.5h-1.5a3 3 0 0 0-2.663 1.618c-.225 .015-.45 .032-.673 .05C8.662 3.295 7.554 4.542 7.502 6ZM13.5 3A1.5 1.5 0 0 0 12 4.5h4.5A1.5 1.5 0 0 0 15 3h-1.5Z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 0 1 3 20.625V9.375Zm9.586 4.594a.75.75 0 0 0-1.172-.938l-2.476 3.096-.908-.907a.75.75 0 0 0-1.06 1.06l1.5 1.5a.75.75 0 0 0 1.116-.062l3-3.75Z" clipRule="evenodd" />
            </svg> {t('profile.sections.summited', { count: summitedMountains.length })}</h2>
            <div className={styles.mountainsGrid}>
              {summitedMountains.slice(0, visibleSummitedCount).map(mountain => (
                <MountainCard
                  key={mountain.id}
                  mountain={mountain}
                />
              ))}
              {summitedMountains.length > visibleSummitedCount && (
                <div className={styles.showMoreContainer}>
                  <button
                    className={styles.showMoreButton}
                    onClick={() => setVisibleSummitedCount(prev => prev + initialCount)}
                  >
                    {t('profile.showMore', 'Show More')}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Uploaded Section */}
        {uploadedMountains.length > 0 && (
          <section className={styles.section}>
            <h2><svg style={{ width: '2rem', height: '2rem', marginBottom: '-0.25rem' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
              <path fillRule="evenodd" d="M10.5 3.75a6 6 0 0 0-5.98 6.496A5.25 5.25 0 0 0 6.75 20.25H18a4.5 4.5 0 0 0 2.206-8.423 3.75 3.75 0 0 0-4.133-4.303A6.001 6.001 0 0 0 10.5 3.75Zm2.03 5.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.72-1.72v4.94a.75.75 0 0 0 1.5 0v-4.94l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3Z" clipRule="evenodd" />
            </svg> {t('profile.sections.uploaded', { count: uploadedMountains.length })}</h2>
            <div className={styles.mountainsGrid}>
              {uploadedMountains.slice(0, visibleUploadedCount).map(mountain => (
                <MountainCard
                  key={mountain.id}
                  mountain={mountain}
                />
              ))}
              {uploadedMountains.length > visibleUploadedCount && (
                <div className={styles.showMoreContainer}>
                  <button
                    className={styles.showMoreButton}
                    onClick={() => setVisibleUploadedCount(prev => prev + initialCount)}
                  >
                    {t('profile.showMore', 'Show More')}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
    </>
  );
}

export default ProfilePage;
